import {
  AlertTriangle,
  Check,
  CircleSlash,
  Clock,
  KeyRound,
  Loader,
  Pencil,
  ShieldAlert,
  Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { AppRegistrationRequestDetail } from "@/lib/api";
import type { ScopeRisk } from "@/lib/app-registration-scopes";
import { cn } from "@/lib/utils";

/**
 * The badges that describe a permission or a request, shared by both sides of
 * the desk.
 *
 * These live here rather than in either feature folder because the requester
 * (`features/home/app-registration-*`) and the approver
 * (`features/admin/app-registrations/`) are looking at *the same permission*
 * and *the same request*. Two copies drifted apart almost immediately — one
 * rendered `High risk`, the other `HIGH` — which is the one thing a risk badge
 * must never do: make the same `Mail.Read` look like two different asks
 * depending on who opened the screen.
 *
 * Wording is deliberately the fuller, self-describing form:
 *
 * - `High risk`, not `HIGH` — a bare tier next to a scope name leaves "high
 *   what?" to colour alone, which fails anyone who cannot use the colour.
 * - `Awaiting approval`, not `Pending` — it names what has to happen next, and
 *   an approver's queue is literally the list of things awaiting approval, so
 *   nothing is lost by saying it on both sides.
 */

const RISK_VARIANT: Record<ScopeRisk, "secondary" | "warning" | "destructive"> = {
  low: "secondary",
  medium: "warning",
  high: "destructive",
};

const RISK_LABEL: Record<ScopeRisk, string> = {
  low: "Low risk",
  medium: "Medium risk",
  high: "High risk",
};

/** Green is the only success colour the badge palette has no variant for. */
const PROVISIONED_CLASSES = "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300";

/** A scope's blast radius, as a badge. Also used for a whole request's worst. */
export function RiskBadge({ risk, className }: { risk: ScopeRisk; className?: string }) {
  return (
    <Badge variant={RISK_VARIANT[risk]} className={className}>
      {RISK_LABEL[risk]}
    </Badge>
  );
}

/**
 * Flags a permission that lets the app administer identity and access —
 * grant permissions or roles, change group membership, take over accounts,
 * rewrite sign-in policy — or act as the user's mailbox: send mail as them,
 * forward it, open it to others. Separate from the risk tier: most of the
 * catalogue is high risk, and this is what tells "reads a lot" from "can
 * escalate or speak for you". Approving a request that holds one needs an
 * explicit acknowledgement.
 */
export function PrivilegedBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant="destructive"
      className={cn("font-semibold uppercase", className)}
      title="Lets the app administer identity and access, or send mail and control the mailbox, as the signed-in user"
    >
      <KeyRound />
      Privileged
    </Badge>
  );
}

/** Flags that a directory admin has to consent before the app works at all. */
export function AdminConsentBadge({ className }: { className?: string }) {
  return (
    <Badge variant="outline" className={className}>
      <ShieldAlert />
      Admin consent
    </Badge>
  );
}

interface AppRegistrationStatusBadgeProps {
  status: AppRegistrationRequestDetail["status"];
  /**
   * What the row asks for. Only `provisioned` reads differently for a
   * decommission — "Provisioned" next to a request to delete something is a
   * contradiction, and the status column is where somebody skims for whether
   * the application still exists.
   */
  requestType?: AppRegistrationRequestDetail["request_type"];
  /**
   * When the registration this row created was decommissioned, if it was. A
   * retired `create` row keeps `status === "provisioned"` -- that is still
   * what happened to the creation -- so without this the badge goes on
   * reading "Provisioned" for an application that no longer exists.
   */
  decommissionedAt?: string | null;
  className?: string;
}

/**
 * Where a request got to.
 *
 * The labels for every state belong in one place: a requester reading
 * `Awaiting approval` and an approver reading `Pending` about the same row is
 * how the two sides stopped agreeing in the first place.
 *
 * `provisioning` is the one that must not fall through to the default. A row
 * being provisioned has already been approved, so rendering it as "Awaiting
 * approval" puts that badge next to "Approved by …" in the same card — the
 * contradiction the status was added to remove.
 */
export function AppRegistrationStatusBadge({
  status,
  requestType,
  decommissionedAt,
  className,
}: AppRegistrationStatusBadgeProps) {
  if (decommissionedAt && requestType !== "delete") {
    return (
      <Badge variant="secondary" className={className}>
        <Trash2 />
        Decommissioned
      </Badge>
    );
  }
  if (status === "provisioned") {
    if (requestType === "delete") {
      return (
        <Badge variant="secondary" className={className}>
          <Trash2 />
          Decommissioned
        </Badge>
      );
    }
    return (
      <Badge className={cn(PROVISIONED_CLASSES, className)}>
        <Check />
        Provisioned
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="destructive" className={className}>
        <AlertTriangle />
        Provisioning failed
      </Badge>
    );
  }
  if (status === "provisioning") {
    return (
      <Badge variant="secondary" className={className}>
        <Loader className="animate-spin" />
        Provisioning
      </Badge>
    );
  }
  if (status === "rejected") {
    return (
      <Badge variant="secondary" className={className}>
        <CircleSlash />
        Rejected
      </Badge>
    );
  }
  return (
    <Badge variant="warning" className={className}>
      <Clock />
      Awaiting approval
    </Badge>
  );
}

export type DeployEnvironment = NonNullable<AppRegistrationRequestDetail["environment"]>;

/** The `NO-AR-<Project>-<TOKEN>` suffix. Exported so the intake form, the
 * requester's list and this badge cannot drift — the reason this module
 * exists at all. */
export const ENVIRONMENT_TOKEN: Record<DeployEnvironment, string> = {
  dev: "DEV",
  test: "TST",
  prod: "PRD",
};

/** What each environment is called in prose. */
export const ENVIRONMENT_LABEL: Record<DeployEnvironment, string> = {
  dev: "Development",
  test: "Test",
  prod: "Production",
};

/**
 * Which environment a registration belongs to.
 *
 * Load-bearing rather than decorative. One app registration serves exactly one
 * environment, so `NO-AR-Leadtime-DEV` and `NO-AR-Leadtime-PRD` are different
 * applications with different client ids and separately granted consent. An
 * approver who cannot see which one they are looking at is being asked to
 * approve production access from a screen that looks identical to a dev one.
 *
 * Production is `destructive` for the same reason: of the three, it is the one
 * where an approval is worth a second look, and colour alone does not carry it
 * — the token and the `title` say which environment it is.
 *
 * `null` renders nothing at all. Requests that predate per-environment
 * registrations have no environment, and inventing one for them ("DEV?") would
 * put a guess where an approver reads a fact.
 */
export function EnvironmentBadge({
  environment,
  className,
}: {
  environment: AppRegistrationRequestDetail["environment"];
  className?: string;
}) {
  if (!environment) {
    return null;
  }
  return (
    <Badge
      variant={environment === "prod" ? "destructive" : "outline"}
      className={cn("font-mono", className)}
      title={`${ENVIRONMENT_LABEL[environment]} environment`}
    >
      {ENVIRONMENT_TOKEN[environment]}
    </Badge>
  );
}

/**
 * What a row asks for, when it is not the ordinary thing.
 *
 * `create` renders nothing: almost every row is one, and a badge saying
 * "Creation" on all of them would be noise that hides the two that matter.
 * An amendment and a decommission are separate rows hanging off the original
 * request — the same table shows all three — so the approver queue and the
 * project view both need to say which kind of decision they are looking at
 * before anything else about the row makes sense.
 */
export function RequestTypeBadge({
  requestType,
  className,
}: {
  requestType: AppRegistrationRequestDetail["request_type"];
  className?: string;
}) {
  if (requestType === "delete") {
    return (
      <Badge variant="destructive" className={className}>
        <Trash2 />
        Decommission
      </Badge>
    );
  }
  if (requestType === "amend") {
    return (
      <Badge variant="outline" className={className}>
        <Pencil />
        Permission change
      </Badge>
    );
  }
  return null;
}
